// Pure helpers for the Inbox, in a module with no imports so Node's test runner
// can check them (web/test/inbox.test.ts).
//
// Nothing here computes a business fact. Whether a ticket is overdue, how long
// is left, whether the clock is paused and whether a reply was in time all come
// from inbox_queue_v1, inbox_ticket_v1 and inbox_kpi_v1. These only turn those
// facts into words and colours.

export type Tone = 'overdue' | 'soon' | 'ok' | 'paused' | 'none'

/** "2d 4h", "5h 10m", "12m". Never "0m": under a minute reads as "under a minute". */
export function formatDuration(seconds: number): string {
  const s = Math.abs(Math.round(seconds))
  if (s < 60) return 'under a minute'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
  return `${m}m`
}

/**
 * The clock on a row, in words. seconds_left is null when nobody is waiting on
 * us, which is not the same as zero: a ticket that has been answered has no
 * clock, it does not have one at nought.
 */
export function clockLabel(row: {
  seconds_left: number | null
  is_paused: boolean
  first_response_at?: string | null
  status?: string
}): { label: string; tone: Tone } {
  if (row.seconds_left === null || row.seconds_left === undefined) {
    if (row.status === 'resolved' || row.status === 'closed') return { label: 'Closed', tone: 'none' }
    return { label: row.first_response_at ? 'Answered' : 'No clock', tone: 'none' }
  }
  if (row.is_paused) {
    return row.seconds_left < 0
      ? { label: `Paused, ${formatDuration(row.seconds_left)} over`, tone: 'paused' }
      : { label: `Paused, ${formatDuration(row.seconds_left)} left`, tone: 'paused' }
  }
  if (row.seconds_left < 0) return { label: `Overdue ${formatDuration(row.seconds_left)}`, tone: 'overdue' }
  if (row.seconds_left < 6 * 3600) return { label: `${formatDuration(row.seconds_left)} left`, tone: 'soon' }
  return { label: `${formatDuration(row.seconds_left)} left`, tone: 'ok' }
}

const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  open: 'Open',
  waiting_on_requester: 'Waiting on requester',
  waiting_on_internal: 'Waiting on Fleetly',
  resolved: 'Resolved',
  closed: 'Closed',
}

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

export const STATUS_OPTIONS = Object.keys(STATUS_LABEL)

const FILTER_LABEL: Record<string, string> = {
  spam: 'Spam',
  newsletter: 'Newsletter or list',
  automated: 'Automated notice',
  manual: 'Marked as not needing an answer',
}

export function filterLabel(reason: string | null): string | null {
  if (!reason) return null
  return FILTER_LABEL[reason] ?? reason
}

/**
 * Splits an email body into what the person wrote and the quoted history under
 * it, so the thread reads as a conversation rather than the same paragraphs
 * repeated in every message. Nothing is thrown away; the quoted part is only
 * folded.
 */
export function splitQuoted(body: string | null): { fresh: string; quoted: string } {
  const text = (body ?? '').replace(/\r\n/g, '\n')
  const lines = text.split('\n')
  let cut = -1
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    if (/^On .{4,200}wrote:\s*$/.test(l.trim())) { cut = i; break }
    if (/^-{2,}\s*(Original Message|Forwarded message)\s*-{2,}/i.test(l.trim())) { cut = i; break }
    if (/^From:\s.+/.test(l) && i + 1 < lines.length && /^(Sent|Date):\s/.test(lines[i + 1])) { cut = i; break }
    if (l.startsWith('>') && lines.slice(i).every((x) => x.startsWith('>') || x.trim() === '')) { cut = i; break }
  }
  // Nothing quoted, or the whole message is a quote (a bare forward): show it all.
  if (cut <= 0) return { fresh: text.trim(), quoted: '' }
  return { fresh: lines.slice(0, cut).join('\n').trim(), quoted: lines.slice(cut).join('\n').trim() }
}

/** "3 of 5", with the share when there is one, for the KPI tile. */
export function shareLabel(part: number, whole: number): string {
  if (whole <= 0) return 'None received yet'
  return `${part} of ${whole} (${Math.round((part / whole) * 100)}%)`
}

/** The median first response, or a plain "no replies yet" when there is none. Never 0. */
export function medianLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return 'No replies yet'
  return formatDuration(seconds)
}

/** How fresh the mailbox sync is, in words, and whether it has stopped. */
export function syncHealth(
  sync: { last_ok_at: string | null; last_error: string | null; last_error_at: string | null } | null,
  now: Date = new Date(),
): { ok: boolean; label: string } {
  if (!sync) return { ok: false, label: 'The mailbox sync has not been set up.' }
  const ok = sync.last_ok_at ? new Date(sync.last_ok_at).getTime() : 0
  const err = sync.last_error_at ? new Date(sync.last_error_at).getTime() : 0
  if (!ok) {
    return { ok: false, label: sync.last_error ? `Not connected yet. ${sync.last_error}` : 'Not connected yet.' }
  }
  const mins = Math.floor((now.getTime() - ok) / 60000)
  if (sync.last_error && err > ok) return { ok: false, label: `Last read ${mins < 1 ? 'just now' : `${formatDuration(mins * 60)} ago`}. Since then: ${sync.last_error}` }
  if (mins > 15) return { ok: false, label: `The mailbox has not been read for ${formatDuration(mins * 60)}. New email may be missing.` }
  return { ok: true, label: mins < 2 ? 'Up to date' : `Read ${formatDuration(mins * 60)} ago` }
}
